import { useRef, useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { colorsSelector } from 'apps/studio/store/selectors/studioSettings'
import { studioSettingsActions } from 'apps/studio/store/reducers/settings/studioSettings'
const { addColors } = studioSettingsActions
import Saturation from '@uiw/react-color-saturation'
import Alpha from '@uiw/react-color-alpha'
import Hue from '@uiw/react-color-hue'
import {
  hexToHsva,
  hsvaToHex,
  hsvaToRgbaString,
  equalColorObjects,
} from '@uiw/color-convert'
import Dropper from 'apps/studio/assets/icons/Dropper.svg'
import Ring from 'apps/studio/assets/icons/Ring.svg'
import { ChevronDownIcon } from '@gocanvas/gcds/icons'
import { getSaturationBorderRadius } from 'apps/studio/lib/utils/color'
import { usePopover, Popover } from 'apps/studio/components/shared/Popover'

export const unhash = str => str.replace(/#/g, '')

const clampValue = (value, min, max) => Math.min(Math.max(value, min), max)

const clampAlpha = hsva => ({
  ...hsva,
  a: clampValue(hsva.a, 0, 1),
})

const createKeyHandler = (hsva, onChange, keyActions) => {
  return e => {
    const action = keyActions[e.code]
    if (action) {
      e.preventDefault()
      e.stopPropagation()

      const newValue = hsva[action.prop] + action.delta
      const clampedValue = clampValue(newValue, action.min, action.max)
      onChange({ ...hsva, [action.prop]: clampedValue })
    }
  }
}

// converts eyedropper output to hsva, catches errors
const EyeDropperButton = ({ onChange }) => {
  const eyeDropperRef = useRef(null)

  useEffect(() => {
    if ('EyeDropper' in window) {
      eyeDropperRef.current = new window.EyeDropper()
    }
  }, [])

  const onClick = () => {
    if (eyeDropperRef.current) {
      eyeDropperRef.current
        .open()
        .then(({ sRGBHex }) => onChange(hexToHsva(sRGBHex)))
        .catch(x => x)
    }
  }

  if ('EyeDropper' in window) {
    return (
      <button className="gcds--button button--secondary color-picker__eyedropper" onClick={onClick}>
        <Dropper className="gcds--icon icon--svg icon--leading" />
      </button>
    )
  }
  return null
}

// change the circle icon color based on the hsva value
const styleRingPointer = hsva => props => {
  const { prefixCls, left, top } = props
  const { s, v, a } = hsva

  let strokeColor = '#fff'
  if (a < 0.1) {
    const grayScale = Math.round(160 + (95 * a) / 0.1)
    strokeColor = `rgb(${grayScale}, ${grayScale}, ${grayScale})`
  } else if (s < 5 && v > 95 && a > 0.8) {
    const adjustedBrightness = v * (1 - s / 100)
    const grayScale = Math.round(255 * (1 - (adjustedBrightness - 95) / 20))
    strokeColor = `rgb(${grayScale}, ${grayScale}, ${grayScale})`
  }
  return (
    <Ring
      className={`${prefixCls}__pointer color-picker__ring`}
      style={{
        position: 'absolute',
        left,
        top,
        color: hsvaToRgbaString(hsva),
        '--stroke-color': strokeColor,
      }}
    />
  )
}

const Swatch = ({ hsva, onClick }) => {
  const rgba = hsvaToRgbaString(hsva)
  const { s, v, a } = hsva
  const isNearlyOpaque = a > 0.8
  const isNearlyWhite = s < 10 && v > 93
  const isNearlyTransparent = a < 0.1
  const showBackground =
    (isNearlyOpaque && isNearlyWhite) || isNearlyTransparent

  const Element = onClick ? 'button' : 'div'

  const props = {
    className: `color-picker__swatch`,
    style: {
      cursor: onClick ? 'pointer' : 'default',
      backgroundColor: rgba,
      '--bgcolor': rgba,
      // Add a faint outline for white colors with high opacity
      ...(showBackground && {
        boxShadow: 'inset 0 0 0 1px rgba(0, 0, 0, 0.1)',
      }),
    },
  }
  if (onClick) {
    props.onClick = onClick
  }
  return <Element {...props} />
}

// handles the turning use of the controls into focus events
const ActiveStateWrapper = ({
  className,
  children,
  onMouseDown: externalMouseDown,
}) => {
  const [isActive, setIsActive] = useState(false)
  const wrapperRef = useRef(null)

  const handleMouseDown = e => {
    if (!isActive) {
      setIsActive(true)
      const interactiveElement = wrapperRef.current?.querySelector(
        '.w-color-interactive'
      )
      if (interactiveElement && interactiveElement !== document.activeElement) {
        interactiveElement.focus()
      }
    }
    if (externalMouseDown) {
      externalMouseDown(e)
    }
  }

  return (
    <div
      ref={wrapperRef}
      className={`${className}${isActive ? ' pick--active' : ''}`}
      onMouseDown={handleMouseDown}
      onMouseUp={() => setIsActive(false)}
    >
      {children}
    </div>
  )
}

const SaturationMap = ({ hsva, onChange, onMouseDown }) => {
  const fullAlphaHsva = { ...hsva, a: 1 }
  const hueOnly = { ...hsva, s: 100, v: 100, a: 1 }
  const { s, v } = hsva

  // sharpen the corner as pointer gets close to it
  const radius = getSaturationBorderRadius(s, v, '1em')

  const keyActions = {
    ArrowRight: { prop: 's', delta: 0.5, min: 0, max: 100 },
    ArrowLeft: { prop: 's', delta: -0.5, min: 0, max: 100 },
    ArrowUp: { prop: 'v', delta: 0.4, min: 0, max: 100 },
    ArrowDown: { prop: 'v', delta: -0.4, min: 0, max: 100 },
  }

  return (
    <ActiveStateWrapper
      className="color-picker__saturation"
      onMouseDown={onMouseDown} // has to be attached here, unlike the sliders
    >
      <Saturation
        hsva={hsva}
        radius={radius}
        style={{
          width: 'auto',
          height: '12.5em',
          '--focus-color': hsvaToRgbaString(hueOnly),
        }}
        pointer={styleRingPointer(fullAlphaHsva)}
        onChange={newColor => onChange({ ...hsva, ...newColor, a: hsva.a })}
        onKeyDown={createKeyHandler(hsva, onChange, keyActions)}
      />
    </ActiveStateWrapper>
  )
}

const increaseH = { prop: 'h', delta: 1, min: 0, max: 360 }
const decreaseH = { prop: 'h', delta: -1, min: 0, max: 360 }

const HueSlider = ({ hsva, onChange, onMouseDown }) => {
  const { h } = hsva
  const fullBrightHsva = { ...hsva, s: 100, v: 100, a: 1 }

  let radius = '1em'

  // this handles sqauring the corner when they get to the edges
  // providing the illusion of going end to end
  if (h === 360) {
    radius = '1em 0.1em 0.1em 1em'
  } else if (h === 0) {
    radius = '0.1em 1em 1em 0.1em'
  } else if (h > 357) {
    radius = '1em 0.8em 0.8em 1em'
  } else if (h < 11) {
    radius = '0.8em 1em 1em 0.8em'
  }

  const keyActions = {
    ArrowRight: increaseH,
    ArrowLeft: decreaseH,
    ArrowUp: increaseH,
    ArrowDown: decreaseH,
  }

  return (
    <ActiveStateWrapper
      className="color-picker__hue-slider color-picker__slider"
      onMouseDown={onMouseDown}
    >
      <Hue
        hue={hsva.h}
        radius={radius}
        height={'14px'}
        pointer={styleRingPointer(fullBrightHsva)}
        onChange={newHue => onChange({ ...hsva, ...newHue })}
        innerProps={{ onKeyDown: createKeyHandler(hsva, onChange, keyActions) }}
      />
    </ActiveStateWrapper>
  )
}

const increaseA = { prop: 'a', delta: 0.01, min: 0, max: 1 }
const decreaseA = { prop: 'a', delta: -0.01, min: 0, max: 1 }

const AlphaSlider = ({ hsva, onChange, onMouseDown }) => {
  const desaturatedHsva = { ...hsva, s: 0, v: 0 }
  const blackToWhiteHsva = { ...hsva, s: 0, v: 100 - hsva.a * 100, a: 1 }
  const { a } = hsva

  let radius = '1em'

  if (a === 1) {
    radius = '1em 0.1em 0.1em 1em'
  } else if (a === 0) {
    radius = '0.1em 1em 1em 0.1em'
  } else if (a >= 0.98) {
    radius = '1em 0.8em 0.8em 1em'
  } else if (a < 0.02) {
    radius = '0.8em 1em 1em 0.8em'
  }

  const keyActions = {
    ArrowRight: increaseA,
    ArrowLeft: decreaseA,
    ArrowUp: increaseA,
    ArrowDown: decreaseA,
  }

  return (
    <ActiveStateWrapper
      className="color-picker__alpha-slider color-picker__slider"
      onMouseDown={onMouseDown}
    >
      <Alpha
        hsva={desaturatedHsva} // black
        radius={radius}
        style={{}}
        height={'14px'}
        pointer={styleRingPointer(blackToWhiteHsva)}
        onChange={newAlpha => onChange({ ...hsva, a: newAlpha.a })}
        innerProps={{ onKeyDown: createKeyHandler(hsva, onChange, keyActions) }}
      />
    </ActiveStateWrapper>
  )
}

const safehexToHsva = hex => {
  try {
    const cleanedHex = hex.trim().replace(/[^A-Fa-f0-9]/g, '')
    if (![3, 6].includes(cleanedHex.length)) {
      return null
    }
    return hexToHsva(cleanedHex)
  } catch (e) {}
}

const EditableHexInput = ({ hsva, onChange, onBlur }) => {
  const hex = unhash(hsvaToHex(hsva))
  const [inputHex, setInputHex] = useState(hex)
  const isFocused = useRef(false)
  const { a } = hsva
  const lastGoodHex = useRef(hex)
  const inputRef = useRef(null)

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
      isFocused.current = true
    }
  }, [])

  useEffect(() => {
    if (hex !== inputHex) {
      if (!isFocused.current) {
        setInputHex(unhash(hsvaToHex(hsva)))
      }
    }
  }, [hex])

  const handleChange = e => {
    const { value } = e.target
    const newValue = (value || '').trim().replace(/[^A-Fa-f0-9]/g, '')
    setInputHex(newValue)
    const safeHex = safehexToHsva(newValue)

    // only update if the hex is valid
    if (safeHex) {
      onChange({ ...safeHex, a })
      if (lastGoodHex.current !== newValue) {
        lastGoodHex.current = newValue
      }
    }
  }

  const handleBlur = e => {
    isFocused.current = false
    const safeHex = safehexToHsva(lastGoodHex.current || hex)
    const safeHsva = { ...safeHex, a }
    if (inputHex.length < 6) {
      setInputHex(unhash(hsvaToHex(safeHsva)))
    }

    onBlur && onBlur(e)
  }

  return (
    <div className="gcds--input-fields input--secondary input--small color-picker--hex-value">
      <div className="input-fields--wrapper">
        <input
          ref={inputRef}
          className="input-fields__input hex-value--input"
          type="text"
          maxLength={6}
          onFocus={e => {
            isFocused.current = true
            e.target.select()
          }}
          value={inputHex}
          onChange={handleChange}
          onBlur={handleBlur}
        />
      </div>
    </div>
  )
}

const ColorControls = ({ hsva, onChange, hasAlpha }) => {
  const { a } = hsva

  return (
    <div className="color-picker__controls">
      <div className="gcds--input-fields input--secondary input--small color-picker--format">
        <div className="input-fields--wrapper">
          <input className="input-fields__input" value={'Hex'} disabled />
          <div className="input-fields__icon-container icon--trailing">
            <ChevronDownIcon />
          </div>
        </div>
      </div>
      <Swatch hsva={hsva} />
      <EditableHexInput hsva={hsva} onChange={onChange} />
      {hasAlpha && (
        <div className="gcds--input-fields input--small input--secondary color-picker--alpha-value">
          <div className="input-fields--wrapper">
            <input
              className="input-fields__input -color-picker__alpha_input"
              type="number"
              maxLength="3"
              min="0"
              max="100"
              step="1"
              value={(a * 100).toFixed(0)}
              onChange={e => {
                const alphaPercent = parseFloat(e.target.value)
                if (!isNaN(alphaPercent)) {
                  onChange(clampAlpha({ ...hsva, a: alphaPercent / 100 }))
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  )
}

const ColorSwatches = ({ children, onChange, colors = [] }) => {
  if (!colors.length) {
    return null
  }

  return (
    <div className="color-picker__swatches">
      <h4>{children}</h4>
      <div className="color-picker__swatches-list">
        {colors.map((color, index) => (
          <Swatch
            hsva={color}
            onClick={() => onChange(color)}
            key={`colorsw-${index}`}
          />
        ))}
      </div>
    </div>
  )
}

const firstChanges = new Set()

export const ColorPickerUI = ({
  id,
  guid = '',
  title = 'Color Picker',
  color: hexValue = '#FFFFFF',
  alpha,
  onChange = () => {},
  styleLevel,
}) => {
  const hasAlpha = alpha != null && alpha !== false
  const styleKey = `${styleLevel}-${id}-${guid}`
  const recentColors = useSelector(colorsSelector)
  const currentHsva = {
    ...hexToHsva(hexValue),
    a: alpha != null ? alpha : 1,
  } // color from the store
  const originalHsva = useRef(currentHsva) // color when control initially opened
  const [hsva, setHsva] = useState(currentHsva) // local color state
  const latestValue = useRef(hsva)
  const recentEscPress = useRef(false)
  const isDragging = useRef(false)

  const dispatch = useDispatch()

  const handleColorChange = hsvaValue => {
    const nextHsva = hasAlpha ? clampAlpha(hsvaValue) : hsvaValue

    hasAlpha
      ? onChange(hsvaToHex(nextHsva), nextHsva.a)
      : onChange(hsvaToHex(nextHsva))
  }

  // Handle starting drag operations
  const handleDragStart = () => {
    isDragging.current = true
    document.addEventListener('mouseup', handleGlobalMouseUp)
  }

  // Handle global mouseup to catch events outside our components
  const handleGlobalMouseUp = e => {
    if (isDragging.current) {
      handleColorChange(latestValue.current)
      isDragging.current = false
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }

  useEffect(() => {
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [])

  // track or respond to key presses
  useEffect(() => {
    let timeout
    const handleKeyDown = e => {
      if (e.key === 'Escape') {
        clearTimeout(timeout)
        recentEscPress.current = true
        e.stopPropagation()
        timeout = setTimeout(() => {
          recentEscPress.current = false
        }, 100) // reset after a short delay
      } else if (e.key === 'Enter' || e.key === ' ') {
        if (!equalColorObjects(currentHsva, latestValue.current)) {
          handleColorChange(latestHsva)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)
    return () => {
      clearTimeout(timeout)
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [])

  useEffect(() => {
    // ensure this is the latest
    latestValue.current = hsva
  }, [hsva])

  // color is "picked" when control is closed
  useEffect(() => {
    return () => {
      // ...if user just pressed escape, revert back to original color
      if (recentEscPress.current) {
        handleColorChange(originalHsva.current)
        return
      }
      const latestHsva = latestValue.current
      if (!equalColorObjects(currentHsva, latestHsva)) {
        handleColorChange(latestHsva)
      }

      // add to recent colors if not already present
      if (
        !recentColors.some(
          c =>
            equalColorObjects(c, latestHsva) ||
            (c.a === 0 && latestHsva.a === 0)
        )
      ) {
        dispatch(addColors({ colors: [latestHsva] }))
      }
    }
  }, [])

  const setCorrectedHsva = newHsva => {
    const normalizedHsva = hasAlpha ? clampAlpha(newHsva) : newHsva

    // ensure we don't update if the value is the same
    if (equalColorObjects(normalizedHsva, hsva)) {
      return
    }
    let adjustedHsva =
      !firstChanges.has(styleKey) && hsva.a === 0
        ? { ...normalizedHsva, a: 1 } // force alpha from 0 to 1 on first change only
        : normalizedHsva
    firstChanges.add(styleKey)
    setHsva(adjustedHsva)
  }

  const commonProps = {
    hsva,
    onChange: setCorrectedHsva,
    onMouseDown: handleDragStart,
  }

  const onPick = hsvaValue => {
    setCorrectedHsva(hsvaValue)
    handleColorChange(hsvaValue)
  }

  return (
    <div className="color-picker">
      {title && <h4 className="color-picker__title">{title}</h4>}
      <SaturationMap {...commonProps} />
      <EyeDropperButton {...commonProps} onChange={onPick} />
      <HueSlider {...commonProps} />
      {hasAlpha && <AlphaSlider {...commonProps} />}
      <ColorControls {...commonProps} onChange={onPick} hasAlpha={hasAlpha} />
      <ColorSwatches colors={recentColors} {...commonProps} onChange={onPick}>
        Recent
      </ColorSwatches>
    </div>
  )
}

export const ColorPicker = props => {
  const { id, color, alpha, label } = props
  const { triggerProps, popoverProps } = usePopover({ id })
  const { isOpen } = popoverProps

  const currentHSVA = {
    ...hexToHsva(color),
    a: alpha != null ? alpha : 1,
  }

  return (
    // TODO: Should ultimately not need this wrapper, but using it for now
    <div className="gcds--dropdown">
      <div className="gcds--input-fields input--secondary input--medium">
        <label className="input-fields__label" htmlFor={`button-${id}`}>
          {label}
        </label>
        <div className="input-fields--wrapper">
          <button
            className={`input-fields__input color-picker__input color-picker__hex-value ${
              isOpen ? 'active' : ''
            }`}
            id={`button-${id}`}
            type="button"
            {...triggerProps}
          >
            <div className="input-fields__icon-container icon--leading">
              <Swatch hsva={currentHSVA} />
            </div>
            {unhash(color)}
            <div className="input-fields__icon-container icon--trailing">
              <ChevronDownIcon className="" />
            </div>
          </button>
        </div>
      </div>
      <Popover
        {...popoverProps}
        className={`${isOpen && 'gcds--card'}  color-picker__popover`}
      >
        <ColorPickerUI {...props} />
      </Popover>
    </div>
  )
}
